import { z } from 'zod';

/**
 * ADR-137 — Bérlés-foglaltság atomicitás az InventoryItem CAS-en (Phase-2 b).
 *
 * A #291 race-fix (`markAsRented` warn→throw, fail-closed) **kiterjesztése** a teljes checkout-atomicitásra.
 * A foglaltság-SoT az **InventoryItem.status** (ADR-135), NEM a `RentalEquipment.status`.
 * A CAS a **PICKUP**-nál fut (`tx.inventoryItem.updateMany WHERE status='AVAILABLE' → RENTED`);
 * ütközéskor `InventoryCasConflictError` → fail-closed rollback + **409**.
 *
 * Ez a schema a FE-facing conflict-shape (409) + a foglaltság-státusz. A CAS-logika BE-oldali.
 */

export const InventoryReservationStatusSchema = z.enum(['AVAILABLE', 'RESERVED', 'RENTED']);
export type InventoryReservationStatus = z.infer<typeof InventoryReservationStatusSchema>;

/** Pickup/confirm kérés — a foglalandó itemek a rentalból derived. */
export const ReservationConfirmSchema = z.object({
  rentalId: z.string().uuid(),
});
export type ReservationConfirm = z.infer<typeof ReservationConfirmSchema>;

/** Egy CAS-ütköző item (párhuzamos kiadás / már kiadva). */
export const ReservationConflictItemSchema = z.object({
  inventoryItemId: z.string().uuid(),
  /** amit kértünk (RENTED) */
  requestedStatus: InventoryReservationStatusSchema,
  /** ami ténylegesen volt a CAS-pillanatban (pl. már RENTED) */
  actualStatus: InventoryReservationStatusSchema,
});
export type ReservationConflictItem = z.infer<typeof ReservationConflictItemSchema>;

/** 409 body — `InventoryCasConflictError` (fail-closed, ADR-137). */
export const ReservationConflictSchema = z.object({
  code: z.literal('INVENTORY_CAS_CONFLICT'),
  conflictingItems: z.array(ReservationConflictItemSchema).min(1),
  message: z.string(),
});
export type ReservationConflict = z.infer<typeof ReservationConflictSchema>;

/** Sikeres foglalás. */
export const ReservationConfirmResultSchema = z.object({
  rentalId: z.string().uuid(),
  reservedInventoryItemIds: z.array(z.string().uuid()),
  status: z.literal('RESERVED'),
});
export type ReservationConfirmResult = z.infer<typeof ReservationConfirmResultSchema>;
